import React, { useState, useEffect, useMemo } from "react";
import { Form, InputGroup, Badge, Nav, Tab } from "react-bootstrap";
import {
  MdAdd,
  MdSearch,
  MdUpload,
  // MdDownload,
} from "react-icons/md";
import { useForm } from "react-hook-form";
import { usersAPI } from "../services/api";
import "./index.css";
// import Select from "react-select";
import CustomAction from "../components/comman/CustomAction";
import { showToast } from "../services/toast";
import EditUserModal from "../components/modals/users/EditUserModal";
import DeleteModal from "../components/modals/delete-modal/DeleteModal";
import ImportUserModal from "../components/modals/users/ImportUserModal";
import SimpleTable from "../components/comman/custom-table/SimpleTable";
import { FaToggleOff, FaToggleOn } from "react-icons/fa";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadType, setUploadType] = useState("create");
  const [importing, setImporting] = useState(false);
  // const [snackbar, setSnackbar] = useState({
  //   open: false,
  //   message: "",
  //   severity: "success",
  // });

  const [roleOptions, setRoleOptions] = useState([]); // [{id, name}]
  const [rolesLoading, setRolesLoading] = useState(false);
  const [key, setKey] = useState("all");

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      mobileNumber: "",
      roles: [], // ✅ important
      isActive: true, // ✅ boolean
      password: "",
    },
  });

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, key]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let params = { search: searchTerm };

      if (key === "active") params.status = "active";
      if (key === "inactive") params.status = "inactive";

      const response = await usersAPI.getUsers(params);

      if (response.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to fetch users"
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = () => {
    if (key === "active") return users.filter((u) => u.isActive === true);
    if (key === "inactive") return users.filter((u) => u.isActive === false);
    return users;
  };

  // 🔹 Toggle handler
  const handleToggle = async (user) => {
    try {
      const newStatus = !user.isActive;

      await usersAPI.updateUser(user.id, { isActive: newStatus });

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, isActive: newStatus } : u))
      );

      showToast(
        "success",
        `User marked as ${newStatus ? "Active" : "Inactive"}`
      );
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to update status"
      );
    }
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRolesLoading(true);
        const res = await usersAPI.getRoles();
        if (res?.success) setRoleOptions(res.data.roles || []);
      } catch (e) {
        showToast(
          "error",
          e?.response?.data?.message || "Failed to load roles"
        );
      } finally {
        setRolesLoading(false);
      }
    };
    fetchRoles();
  }, []);

  const handleAdd = () => {
    setEditingUser(null);
    reset({
      name: "",
      email: "",
      mobileNumber: "",
      roles: [], // ✅ empty
      isActive: true, // ✅ default active
      password: "",
    });
    setOpenDialog(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    reset({
      name: user.name,
      email: user.email,
      mobileNumber: user.mobileNumber || "",
      roles: (user.roles || []).map((r) => String(r.id)),
      isActive: user.isActive,
    });
    setOpenDialog(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await usersAPI.deleteUser(userToDelete.id);
      // toast.success("User deleted successfully");
      showToast("success", "User deleted successfully");
      fetchUsers();
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to delete user"
      );
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (editingUser) {
        const payload = {
          ...data,
          roleIds: data?.roles || [],
        };
        if (data.updatePassword) {
          payload.password = data.updatePassword;
        }
        delete payload.updatePassword;

        await usersAPI.updateUser(editingUser.id, payload);
        showToast("success", "User updated successfully");
      } else {
        await usersAPI.createUser({
          ...data,
          roleIds: data?.roles || [],
        });
        showToast("success", "User created successfully");
      }

      setOpenDialog(false);
      fetchUsers();
    } catch (error) {
      showToast("error", error.response?.data?.message || "Operation failed");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setImporting(true); // loader start
    try {
      let response;
      if (uploadType === "create") {
        response = await usersAPI.importUsers(file);
      } else {
        response = await usersAPI.updateUsersFromExcel(file);
      }

      if (response.success) {
        showToast(
          "success",
          `${
            uploadType === "create" ? "Users imported" : "Users updated"
          } successfully`
        );

        setOpenUploadDialog(false); // modal band
        fetchUsers();
      } else {
        showToast("error", "Something went wrong!");
      }
    } catch (error) {
      showToast("error", error.response?.data?.message || "Upload failed");
    } finally {
      setImporting(false); // loader stop
    }
  };

  const userColumns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        accessor: "name",
        minWidth: 160,
        sortType: "text",
        cell: (v) => v || "—",
      },
      {
        id: "email",
        header: "Email",
        accessor: "email",
        minWidth: 200,
        sortType: "text",
        cell: (v) =>
          v ? (
            <a href={`mailto:${v}`} className="text-decoration-none">
              {v}
            </a>
          ) : (
            "—"
          ),
      },
      {
        id: "mobile",
        header: "Mobile",
        accessor: "mobileNumber",
        minWidth: 140,
        sortType: "text",
        cell: (v) => v || "—",
      },
      {
        id: "roles",
        header: "Roles",
        minWidth: 200,
        sortable: false,
        cell: (_v, row) => (
          <div className="d-flex flex-wrap gap-1">
            {(row.roles || []).map((role, idx) => (
              <Badge key={idx} bg="secondary">
                {role.name}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessor: "isActive",
        minWidth: 160,
        sortType: "text",
        cell: (v, row) => (
          <div
            className="d-flex align-items-center gap-2"
            style={{ cursor: "pointer" }}
            onClick={() => handleToggle(row)}
          >
            {row.isActive ? (
              <>
                <FaToggleOn size={24} color="green" />
                <span className="text-success fw-bold">Active</span>
              </>
            ) : (
              <>
                <FaToggleOff size={24} color="gray" />
                <span className="text-danger fw-bold">Inactive</span>
              </>
            )}
          </div>
        ),
      },

      {
        id: "createdAt",
        header: "Created At",
        accessor: "createdAt", // change to "createdAt" if your API uses camelCase
        minWidth: 160,
        sortType: "date",
        cell: (v, row) => {
          // fallback if API returns createdAt instead of created_at
          const raw = v ?? row?.created_at;
          if (!raw) return "—";
          const dt = new Date(raw);
          if (isNaN(+dt)) return "—";

          const dateStr = dt.toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          const timeStr = dt.toLocaleString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <div className="d-flex flex-column lh-sm gap-1">
              <strong>{dateStr}</strong>
              <p className="m-0">{timeStr}</p>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        minWidth: 130,
        sortable: false,
        isActions: true, // <- lets SimpleTable render Edit/Delete buttons
        className: "text-nowrap",
      },
    ],
    []
  );

  return (
    <div className="h-100 overflow-auto position-relative z-1">
      {/* <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Users Management</h4>
        <div>
          <Button
            variant="primary"
            className="me-2"
            onClick={() => setOpenUploadDialog(true)}
          >
            <MdUpload className="me-1" /> Upload Excel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <MdAdd className="me-1" /> Add User
          </Button>
        </div>
      </div> */}
      <div className="d-flex justify-content-between align-items-center mb-1 p-3 sticky-top bg-body">
        <h4 className="mb-0">User Management</h4>
        <div className="d-flex flex-end gap-2">
          <InputGroup className="search-bar border-primary rounded-2">
            <Form.Control
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <InputGroup.Text>
              <MdSearch />
            </InputGroup.Text>
          </InputGroup>
          <CustomAction
            actions={[
              {
                label: "Import Excel",
                icon: MdUpload,
                onClick: () => setOpenUploadDialog(true),
              },
              {
                label: "Add User",
                icon: MdAdd,
                onClick: handleAdd,
              },
            ]}
          />
        </div>
      </div>

      <div className="content-cover px-3 position-relative">
        <Tab.Container
          id="tabs"
          defaultActiveKey="all"
          transition={false}
          activeKey={key}
          onSelect={(k) => setKey(k)}
        >
          <Nav
            variant="pills"
            className="mb-3 d-flex gap-2 sticky-tp border-bottom bg-body"
          >
            <Nav.Item>
              <Nav.Link eventKey="all">All</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="active">Active</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="inactive">InActive</Nav.Link>
            </Nav.Item>
          </Nav>
        </Tab.Container>
        {/* <div className="table-cover">
          {loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <Table hover bordered className="position-relative z-1 mb-0">
                <thead className="sticky-top">
                  <tr>
                    
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Roles</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      
                      <td>{u.name}</td>
                      <td>{u.email}</td>
                      <td>{u.mobileNumber || ""}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {(u.roles || []).map((role, idx) => (
                            <Badge key={idx} bg="secondary">
                              {role.name}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td>
                        <Badge bg={u.isActive ? "success" : "secondary"}>
                          {u.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td>
                        {u.createdAt
                          ? new Date(u.createdAt).toLocaleDateString()
                          : ""}
                      </td>
                      <td>
                        <div className="d-flex">
                          <Button
                            size="sm"
                            className="table-btn me-2"
                            title="Edit"
                            onClick={() => handleEdit(u)}
                          >
                            <MdEdit />
                          </Button>

                          <Button
                            size="sm"
                            title="Delete"
                            className="table-btn"
                            variant="outline-danger"
                            onClick={() => {
                              setUserToDelete(u);
                              setShowDeleteModal(true);
                            }}
                          >
                            <MdDelete />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>{" "}
            </>
          )}
        </div> */}

        <SimpleTable
          loading={loading}
          // data={users}
          data={filteredUsers()}
          columns={userColumns}
          keyField="id"
          responsive={true}
          emptyMessage="No records found"
          onEdit={(row) => handleEdit(row)}
          onDelete={(row) => {
            setUserToDelete(row);
            setShowDeleteModal(true);
          }}
        />

        {/* <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          loading={loading}
          onPageChange={setCurrentPage}
          className={" sticky-bottom"}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setCurrentPage={setCurrentPage}
          total={total}
          perPageOptions={DEFAULT_PAGE_OPTIONS}
        /> */}
      </div>
      {/* Add/Edit User Modal */}
      <EditUserModal
        show={openDialog}
        onHide={() => setOpenDialog(false)}
        isEdit={editingUser}
        onSubmit={handleSubmit(onSubmit)}
        control={control}
        errors={errors}
        roleOptions={roleOptions}
      />

      {/* delete user modal */}
      {/* Delete Confirmation Modal */}
      <DeleteModal
        confirmDelete={handleDelete}
        show={showDeleteModal}
        title={"Delete User"}
        onHide={() => setShowDeleteModal(false)}
        onCancel={() => setShowDeleteModal(false)}
        message={`Are you sure you want to delete <strong>${userToDelete?.name}</strong>?`}
      />

      {/* <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{userToDelete?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal> */}

      {/* Upload Excel Modal */}
      <ImportUserModal
        show={openUploadDialog}
        onHide={() => setOpenUploadDialog(false)}
        handleFileUpload={handleFileUpload}
        uploadType={uploadType}
        setUploadType={setUploadType}
        importing={importing}
      />
    </div>
  );
};

export default Users;
